'use client';

import * as React from 'react';
import { useTenant } from '@/hooks/use-tenant';
import {
    useGetForumsQuery,
    useGetExternalForumsQuery,
    useCreateForumMutation,
    useCreateExternalForumMutation,
    useUpdateForumMutation,
    useUpdateExternalForumMutation,
    useDeleteForumMutation,
    useDeleteExternalForumMutation,
    useCreateForumCommentMutation,
    useCreateExternalForumCommentMutation,
    useUpdateForumCommentMutation,
    useUpdateExternalForumCommentMutation,
    useDeleteForumCommentMutation,
    useDeleteExternalForumCommentMutation,
} from '@/state/project/project-api';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
    Loader2,
    Plus,
    Trash2,
    MessageSquare,
    MessageCircle,
    Clock,
    User,
    Send,
    CornerDownRight,
    Pencil,
    X,
    Check,
} from 'lucide-react';
import type { Forum, ForumComment } from '@/types';
import { RichTextEditor } from '@/components/rich-text';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import { formatDate } from 'date-fns';
import { DATE_FORMAT_WITH_TIME } from '@/constants';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { ForumAuthor } from '@/types/api/forum.types';

const getAuthorEmail = (author: string | ForumAuthor | undefined, creatorEmail?: string) => {
    if (creatorEmail) return creatorEmail;
    if (typeof author === 'object' && author?.email) return author.email;
    if (typeof author === 'string') return author;
    return 'Unknown';
};

const ForumCommentItem = ({
    comment,
    projectId,
    onDelete,
    isExternal = false,
    isReadOnly = false,
}: {
    comment: ForumComment;
    projectId: number;
    onDelete: (id: number) => void;
    isExternal?: boolean;
    isReadOnly?: boolean;
}) => {
    const { tenant } = useTenant();
    const [isEditing, setIsEditing] = React.useState(false);
    const [editText, setEditText] = React.useState(comment.text);

    const [updateInternal, { isLoading: isUpdatingInternal }] = useUpdateForumCommentMutation();
    const [updateExternal, { isLoading: isUpdatingExternal }] = useUpdateExternalForumCommentMutation();

    const isUpdating = isExternal ? isUpdatingExternal : isUpdatingInternal;

    const handleUpdate = async () => {
        if (!editText.trim()) return;
        try {
            if (isExternal) {
                await updateExternal({
                    commentId: comment.id,
                    projectId,
                    data: { text: editText },
                }).unwrap();
            } else {
                await updateInternal({
                    tenant: tenant!,
                    commentId: comment.id,
                    projectId,
                    data: { text: editText },
                }).unwrap();
            }
            setIsEditing(false);
            toast.success('Comment updated');
        } catch (error) {
            toast.error('Failed to update comment');
            console.error('Update comment error:', error);
        }
    };

    return (
        <div className="group relative flex gap-3 py-4 first:pt-0 last:pb-0">
            <div className="flex-shrink-0 pt-0.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted shadow-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                </div>
            </div>
            <div className="flex-grow space-y-1.5 min-w-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                        <span className="font-bold text-foreground">{getAuthorEmail(comment.author, comment.creator_email)}</span>
                        <span className="text-[10px] text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">
                            {formatDate(new Date(comment.created_at), DATE_FORMAT_WITH_TIME)}
                        </span>
                        {isEditing && <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Editing</span>}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        {(!isEditing && !isReadOnly) ? (
                            <>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setIsEditing(true)}
                                    className="h-8 w-8 text-muted-foreground hover:bg-primary/5 hover:text-primary"
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete comment?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                Are you sure you want to delete this comment?
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => onDelete(comment.id)}
                                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                            >
                                                Delete
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </>
                        ) : (
                            <div className="flex items-center gap-1">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                        setIsEditing(false);
                                        setEditText(comment.text);
                                    }}
                                    className="h-8 w-8 text-muted-foreground"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    disabled={isUpdating || !editText.trim() || editText === comment.text}
                                    onClick={handleUpdate}
                                    className="h-8 w-8 text-primary"
                                >
                                    {isUpdating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {isEditing ? (
                    <div className="mt-2 rounded-xl border border-primary/20 bg-background overflow-hidden ring-2 ring-primary/5">
                        <RichTextEditor
                            value={editText}
                            onChange={setEditText}
                            placeholder="Edit your comment..."
                            className="min-h-[100px]"
                        />
                    </div>
                ) : (
                    <div className="rounded-xl bg-muted/30 p-3 ring-1 ring-border/5 group-hover:bg-muted/50 transition-colors">
                        <ReadOnlyContentDisplay
                            html={comment.text}
                            className="prose prose-sm dark:prose-invert max-w-none text-foreground/90 text-sm leading-relaxed"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const ForumItem = ({
    forum,
    projectId,
    projectRefId,
    onDeleteForum,
    isExternal = false,
    isReadOnly = false,
}: {
    forum: Forum;
    projectId: number;
    projectRefId?: number;
    onDeleteForum: (id: number) => void;
    isExternal?: boolean;
    isReadOnly?: boolean;
}) => {
    const { tenant } = useTenant();

    // Discussion Edit State
    const [isEditingForum, setIsEditingForum] = React.useState(false);
    const [editForumData, setEditForumData] = React.useState({
        title: forum.title,
        description: forum.description,
    });

    const [updateInternalForum] = useUpdateForumMutation();
    const [updateExternalForum] = useUpdateExternalForumMutation();
    const [isUpdatingForum, setIsUpdatingForum] = React.useState(false);

    // Comment State
    const [isCommenting, setIsCommenting] = React.useState(false);
    const [commentText, setCommentText] = React.useState('');

    const [createInternalComment, { isLoading: isCreatingInternalComment }] = useCreateForumCommentMutation();
    const [createExternalComment, { isLoading: isCreatingExternalComment }] = useCreateExternalForumCommentMutation();

    const isCreatingComment = isExternal ? isCreatingExternalComment : isCreatingInternalComment;

    const [deleteInternalComment] = useDeleteForumCommentMutation();
    const [deleteExternalComment] = useDeleteExternalForumCommentMutation();

    const handleUpdateForum = async () => {
        if (!editForumData.title.trim() || !editForumData.description.trim()) return;
        setIsUpdatingForum(true);
        try {
            if (isExternal) {
                await updateExternalForum({
                    forumId: forum.id,
                    projectId: projectRefId || projectId,
                    data: {
                        title: editForumData.title,
                        description: editForumData.description,
                    },
                }).unwrap();
            } else {
                await updateInternalForum({
                    tenant: tenant!,
                    forumId: forum.id,
                    projectId,
                    data: {
                        title: editForumData.title,
                        description: editForumData.description,
                    },
                }).unwrap();
            }
            setIsEditingForum(false);
            toast.success('Discussion updated');
        } catch (error) {
            toast.error('Failed to update discussion');
            console.error('Update forum error:', error);
        } finally {
            setIsUpdatingForum(false);
        }
    };

    const handleCreateComment = async () => {
        if (!commentText.trim()) return;

        try {
            if (isExternal) {
                await createExternalComment({
                    projectId: projectRefId || projectId,
                    data: {
                        text: commentText,
                        forum: forum.id,
                        parent: null,
                    },
                }).unwrap();
            } else {
                await createInternalComment({
                    tenant: tenant!,
                    projectId,
                    data: {
                        text: commentText,
                        forum: forum.id,
                        project: projectId,
                    },
                }).unwrap();
            }
            setCommentText('');
            setIsCommenting(false);
            toast.success('Comment added');
        } catch (error) {
            toast.error('Failed to add comment');
            console.error('Create comment error:', error);
        }
    };

    const handleDeleteComment = async (commentId: number) => {
        try {
            if (isExternal) {
                await deleteExternalComment({
                    commentId,
                    projectId: projectRefId || projectId,
                }).unwrap();
            } else {
                await deleteInternalComment({
                    tenant: tenant!,
                    commentId,
                    projectId,
                }).unwrap();
            }
            toast.success('Comment deleted');
        } catch (error) {
            toast.error('Failed to delete comment');
            console.error('Delete comment error:', error);
        }
    };

    return (
        <div className="relative">
            {/* Timeline Node */}
            <div className="absolute -left-[25px] top-6 z-10 sm:-left-[33px]">
                <div className="flex h-6 w-6 items-center justify-center rounded-full border-4 border-background bg-primary shadow-sm ring-2 ring-primary/20">
                    <MessageCircle className="h-2.5 w-2.5 text-primary-foreground" />
                </div>
            </div>

            <Card className="group border-border/40 bg-card/80 backdrop-blur-sm transition-all duration-300 hover:border-primary/40 hover:shadow-xl hover:shadow-black/5">
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
                    <div className="space-y-2 flex-grow min-w-0">
                        {isEditingForum ? (
                            <div className="space-y-3 pr-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black text-primary uppercase tracking-widest bg-primary/10 px-2 py-0.5 rounded-full">Editing Mode</span>
                                </div>
                                <Input
                                    value={editForumData.title}
                                    onChange={(e) => setEditForumData(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="Discussion title..."
                                    className="text-lg font-bold bg-background/50"
                                />
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-2">
                                    <CardTitle className="text-xl font-bold group-hover:text-primary transition-colors truncate">{forum.title}</CardTitle>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1.5 font-medium">
                                        <User className="h-3.5 w-3.5" />
                                        {getAuthorEmail(forum.author, forum.creator_email)}
                                    </span>
                                    <span className="flex items-center gap-1.5 font-medium text-primary/70">
                                        <Clock className="h-3.5 w-3.5" />
                                        {forum.created_at
                                            ? formatDate(new Date(forum.created_at), DATE_FORMAT_WITH_TIME)
                                            : 'No date'}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex items-center gap-1">
                        {(!isEditingForum && !isReadOnly) ? (
                            <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setIsEditingForum(true)}
                                    className="h-9 w-9 text-muted-foreground hover:bg-primary/5 hover:text-primary"
                                >
                                    <Pencil className="h-4.5 w-4.5" />
                                </Button>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-9 w-9 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                        >
                                            <Trash2 className="h-4.5 w-4.5" />
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete Discussion?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This action cannot be undone. This will permanently delete the discussion topic and all associated content.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => onDeleteForum(forum.id)}
                                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                            >
                                                Delete
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setIsEditingForum(false);
                                        setEditForumData({ title: forum.title, description: forum.description });
                                    }}
                                    className="h-8 text-xs px-3"
                                >
                                    <X className="mr-1.5 h-3.5 w-3.5" />
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    disabled={isUpdatingForum || !editForumData.title.trim() || !editForumData.description.trim()}
                                    onClick={handleUpdateForum}
                                    className="h-8 text-xs px-3"
                                >
                                    {isUpdatingForum ? (
                                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <Check className="mr-1.5 h-3.5 w-3.5" />
                                    )}
                                    Save Changes
                                </Button>
                            </div>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    {isEditingForum ? (
                        <div className="rounded-xl border border-primary/20 bg-background overflow-hidden ring-4 ring-primary/5 shadow-inner">
                            <RichTextEditor
                                value={editForumData.description}
                                onChange={(html) => setEditForumData(prev => ({ ...prev, description: html }))}
                                placeholder="Edit discussion content..."
                                className="min-h-[200px]"
                            />
                        </div>
                    ) : (
                        <div className="rounded-xl bg-muted/30 p-5 ring-1 ring-border/5 group-hover:bg-muted/50 transition-colors">
                            <ReadOnlyContentDisplay
                                html={forum.description}
                                className="prose prose-sm dark:prose-invert max-w-none text-foreground/90 leading-relaxed"
                            />
                        </div>
                    )}

                    {/* Comments Section */}
                    <div className="space-y-4 pt-2">
                        <div className="flex items-center justify-between px-1">
                            <div className="flex items-center gap-2">
                                <CornerDownRight className="h-4 w-4 text-muted-foreground/60" />
                                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground/80">
                                    Comments ({forum.forum_comments?.length || 0})
                                </h4>
                            </div>
                        </div>

                        <div className="divide-y divide-border/30 rounded-2xl bg-muted/10 p-4 ring-1 ring-border/5 shadow-inner">
                            {forum.forum_comments && forum.forum_comments.length > 0 ? (
                                forum.forum_comments.map((comment) => (
                                    <ForumCommentItem
                                        key={comment.id}
                                        comment={comment}
                                        projectId={projectId}
                                        onDelete={handleDeleteComment}
                                        isExternal={isExternal}
                                        isReadOnly={isReadOnly}
                                    />
                                ))
                            ) : (
                                <p className="py-2 text-center text-xs text-muted-foreground/60">No comments yet</p>
                            )}
                        </div>

                        {!isCommenting && !isReadOnly ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setIsCommenting(true)}
                                className="h-8 text-xs font-medium text-muted-foreground hover:bg-primary/5 hover:text-primary transition-all"
                            >
                                <Plus className="mr-1.5 h-3 w-3" />
                                Add a comment
                            </Button>
                        ) : (
                            <Card className="border-primary/20 bg-primary/[0.01] shadow-inner overflow-hidden border-none bg-transparent">
                                <CardContent className="p-3 space-y-3">
                                    <div className="min-h-[140px] rounded-lg border border-border/40 bg-background overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                                        <RichTextEditor
                                            value={commentText}
                                            onChange={setCommentText}
                                            placeholder="Write a comment..."
                                            className="min-h-[120px]"
                                        />
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setIsCommenting(false);
                                                setCommentText('');
                                            }}
                                            disabled={isCreatingComment}
                                            className="h-8 text-xs px-3"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={handleCreateComment}
                                            disabled={isCreatingComment || !commentText.trim()}
                                            className="h-8 text-xs px-3 gap-1.5"
                                        >
                                            {isCreatingComment ? (
                                                <Loader2 className="h-3 w-3 animate-spin" />
                                            ) : (
                                                <Send className="h-3 w-3" />
                                            )}
                                            Post Comment
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export const ProjectForums = ({
    projectId,
    projectRefId,
    isExternal = false,
    isReadOnly = false,
}: {
    projectId: number;
    projectRefId?: number;
    isExternal?: boolean;
    isReadOnly?: boolean;
}) => {
    const { tenant } = useTenant();
    const [isAdding, setIsAdding] = React.useState(false);
    const [newDiscussion, setNewDiscussion] = React.useState({
        title: '',
        description: '',
    });

    const {
        data: internalForumData,
        isLoading: internalForumsLoading,
    } = useGetForumsQuery({ tenant: tenant!, projectId }, { skip: isExternal });

    const {
        data: externalForumData,
        isLoading: externalForumsLoading,
    } = useGetExternalForumsQuery({ projectId: projectRefId || projectId }, { skip: !isExternal });

    const forumsLoading = isExternal ? externalForumsLoading : internalForumsLoading;
    const forumData = isExternal ? externalForumData : internalForumData;
    const forums = forumData?.results || [];

    const [createInternalForum, { isLoading: isCreatingInternal }] = useCreateForumMutation();
    const [createExternalForum, { isLoading: isCreatingExternal }] = useCreateExternalForumMutation();

    const isCreating = isExternal ? isCreatingExternal : isCreatingInternal;

    const [deleteInternalForum] = useDeleteForumMutation();
    const [deleteExternalForum] = useDeleteExternalForumMutation();

    const handleCreate = async () => {
        if (!newDiscussion.title.trim() || !newDiscussion.description.trim()) {
            toast.error('Title and description are required.');
            return;
        }

        try {
            if (isExternal) {
                await createExternalForum({
                    data: {
                        title: newDiscussion.title,
                        description: newDiscussion.description,
                        project: projectRefId || projectId,
                    },
                }).unwrap();
            } else {
                await createInternalForum({
                    tenant: tenant!,
                    data: {
                        title: newDiscussion.title,
                        description: newDiscussion.description,
                        project: projectId,
                    },
                }).unwrap();
            }
            toast.success('Discussion started successfully');
            setNewDiscussion({ title: '', description: '' });
            setIsAdding(false);
        } catch (error) {
            toast.error('Failed to start discussion');
            console.error('Create forum error:', error);
        }
    };

    const handleDeleteForum = async (forumId: number) => {
        try {
            if (isExternal) {
                await deleteExternalForum({ forumId, projectId }).unwrap();
            } else {
                await deleteInternalForum({ tenant: tenant!, forumId, projectId }).unwrap();
            }
            toast.success('Discussion deleted');
        } catch (error) {
            toast.error('Failed to delete discussion');
            console.error('Delete forum error:', error);
        }
    };

    if (forumsLoading) {
        return (
            <div className="flex h-[400px] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className=" space-y-8 pb-12">
            {/* Header & Add Button */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight">Project Forum</h2>
                    <p className="text-sm text-muted-foreground">
                        Share ideas and discuss progress with your team.
                    </p>
                </div>
                {!isAdding && !isReadOnly && (
                    <Button onClick={() => setIsAdding(true)} className="gap-2 shadow-sm transition-all hover:shadow-md">
                        <Plus className="h-4 w-4" />
                        Start Discussion
                    </Button>
                )}
            </div>

            {/* New Discussion Form */}
            {isAdding && (
                <Card className="border-primary/20 bg-primary/[0.01] shadow-lg ring-1 ring-primary/5 transition-all">
                    <CardHeader>
                        <CardTitle>Start New Discussion</CardTitle>
                        <CardDescription>
                            Write a title and description for your new discussion topic.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-sm font-semibold">Discussion Title</label>
                            <Input
                                placeholder="Enter discussion title..."
                                value={newDiscussion.title}
                                onChange={(e) =>
                                    setNewDiscussion((prev) => ({ ...prev, title: e.target.value }))
                                }
                                className="bg-background border-border/60 focus-visible:ring-primary shadow-sm mt-2"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-semibold">Description</label>
                            <div className="min-h-[220px] rounded-xl border border-border/60 bg-background overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary/50 transition-all shadow-sm">
                                <RichTextEditor
                                    value={newDiscussion.description}
                                    onChange={(html) =>
                                        setNewDiscussion((prev) => ({ ...prev, description: html }))
                                    }
                                    placeholder="Share your thoughts..."
                                    className="min-h-[200px]"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setIsAdding(false)}
                                disabled={isCreating}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleCreate} disabled={isCreating} className="gap-2 px-6">
                                {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                Post Discussion
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Discussion List */}
            <div className="relative space-y-12 pl-8 sm:pl-12">
                {/* Vertical Timeline Bar */}
                {forums.length > 0 && (
                    <div className="absolute left-4 top-2 bottom-0 w-[2px] bg-gradient-to-b from-primary/40 via-primary/20 to-transparent sm:left-6" />
                )}

                {forums.length > 0 ? (
                    forums.map((forum: Forum) => (
                        <ForumItem
                            key={forum.id}
                            forum={forum}
                            projectId={projectId}
                            projectRefId={projectRefId}
                            onDeleteForum={handleDeleteForum}
                            isExternal={isExternal}
                            isReadOnly={isReadOnly}
                        />
                    ))
                ) : !isAdding && (
                    <div className="flex min-h-[300px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border/40 bg-muted/20 px-6 text-center -ml-8 sm:-ml-12 shadow-inner">
                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background shadow-md">
                            <MessageSquare className="h-8 w-8 text-primary shadow-sm" />
                        </div>
                        <h3 className="text-lg font-bold">No discussions yet</h3>
                        <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                            Be the first to start a conversation with the team about this project.
                        </p>
                        <Button
                            variant="outline"
                            onClick={() => setIsAdding(true)}
                            className="mt-6 gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Start First Discussion
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
